/**
 * EventsFacade — typed API over an event-shaped dataSource.
 *
 * Wraps a lazy-rebuild `EventsIndex` that keeps hydrated
 * `EventItemTimed` / `EventItemAllDay` instances plus
 * client-expanded RRULE instances. Read queries delegate to the
 * index (id lookups, UTC-ms range overlap, DSE full-day overlap).
 *
 * The facade carries no writer: an app writes events through
 * `client.events`. The SDK's own cell-level event writes are
 * `eventsWriterOf(facade)` (`@tento-lona/sheets/internal-sdk`): `put` and
 * `remove`, persisted through the row's event chokepoint (`writeEvent`, which
 * places the cell in the lane the event's temporal kind demands and puts both
 * lanes back on a refusal), and `removeLocal`, which clears the cell locally.
 *
 * Obtained via `row.as(EVENTS_FACADE)`. Attaches to any source
 * row with `dtype="obj"` + `cellCardinality="multi"`; the probe
 * gate is enforced at query time (empty facade when no cells
 * pass the `EVENT_SHAPE` probe).
 */

import type { EventId } from "../ids/ids.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import type { Option } from "../core/option.ts";
import { NaiveDate, type DateTime, type Utc } from "@tento-chrono";
import { invariant } from "@tento-core/assert";
import { Tracing } from "@tento-core/tracing";

import type { CalendarEvent } from "../core/dtype.ts";
import { FacadeKey, type FacadeFactory } from "./facade.ts";
import {
  ColumnDataChanged,
  type Invalidation,
} from "../tx/invalidation.ts";
import {
  EVENT_SHAPE,
  EventItemAllDay,
  EventItemTimed,
  EventsIndex,
  eventValues,
} from "../indices/events-index.ts";
import type { EventItem } from "../indices/events/event-item.ts";
import type { EventPatch } from "../indices/events/event-patch.ts";
import { CellLane } from "../stores/cell-lane.ts";
import type { EventOrigin } from "../tx/event-origin.ts";
import { ListDataSource } from "../rows/variants/list-data-source.ts";
import type { SourceRow } from "../rows/variants/source-row.ts";
import { sheetOf } from "../sheet/sheet-stores.ts";
import { sourceWriterOf } from "../rows/variants/source-row.ts";
import { CellId } from "../ids/ids.ts";
import { deepFrozen } from "../core/frozen-collections.ts";
import { Internals } from "../core/internals.ts";

/** The facade key that reaches a source row's `EventsFacade`. */
export const EVENTS_FACADE = new FacadeKey<EventsFacade>("events");
export { EVENT_SHAPE };
export type { EventItem };
export { EventItemTimed, EventItemAllDay };

/** `EventsFacade`'s friend record (`Internals`): its constructor registers each instance. */
type EventsFacadeInternals = {
  readonly dispose: (facade: EventsFacade) => void;
  readonly writer: (facade: EventsFacade) => EventsFacade.Writer;
};
const eventsFacadeInternals = new Internals<EventsFacade, EventsFacadeInternals>("EventsFacade");

/**
 * Tear down `facade`'s store subscription. The SDK-private door onto the
 * private `dispose` — a facade is cached per row and shared by every reader
 * of it (`row.as(key)`), so a consumer must not be able to stop it under the
 * others. Not on a barrel.
 */
export function disposeEventsFacade(facade: EventsFacade): void {
  eventsFacadeInternals.of(facade).dispose(facade);
}

/**
 * **The writers of an events facade** — `put` / `remove` (persisted) and
 * `removeLocal` (local only), each one event cell of the facade's row, and
 * `applyUpdate` (a patch of a held event, persisted as `put`). The
 * SDK-private door: the facade a read hands out (`row.as(EVENTS_FACADE)`)
 * carries no writer, and an app writes events through `client.events`. On
 * `@tento-lona/sheets/internal-sdk` and `@tento-lona/sheets/internal-testing`,
 * not on a barrel.
 */
export function eventsWriterOf(facade: EventsFacade): EventsFacade.Writer {
  return eventsFacadeInternals.of(facade).writer(facade);
}

/** What `EventsIndex.apply` takes: the stored events to upsert and the ids to remove. */
type IndexChanges = Parameters<EventsIndex["apply"]>[0];

/** One stored copy of an event: its value and its order within its lane. */
type Placed = { readonly event: CalendarEvent; readonly order: number };

/**
 * A source row's stored events as `eventValues(row, null)` answers them —
 * both cell lanes, one value per event id: a timepoint cell over a date cell,
 * and within a lane the later cell (a later day; a later start) — kept per
 * place (a date column, or a UTC day of timepoint cells), so a change
 * announced for a column re-reads that column and not the row's history.
 * Holds, per id, the value the index was given and its render signature.
 */
class StoredEvents {
  /** Whether the row was read at all yet. */
  read = false;
  /** Per place key (`d` + a date column, `t` + a UTC day number), the events there by id. */
  readonly #places = new Map<string, Map<EventId, Placed>>();
  /** The place keys holding each event id. */
  readonly #placesOf = new Map<EventId, Set<string>>();
  /** The date column of each `d` place key, and the UTC day of each `t` one. */
  readonly #columns = new Map<string, NaiveDate.Partial>();
  readonly #days = new Map<string, NaiveDate>();
  /** Per id, the value the index holds and its render signature. */
  readonly #held = new Map<EventId, { readonly event: CalendarEvent; readonly sig: string }>();
  /** Signature per stored value: values are never mutated in place, so an unchanged one is signed once. */
  readonly #sigs = new WeakMap<CalendarEvent, string>();
  /** Per id, the stored value now (`eventValues`' answer), whatever the index was given. */
  readonly #current = new Map<EventId, CalendarEvent>();
  /** `#current` as a list, built at the first read after it changed. */
  #currentList: Option<readonly CalendarEvent[]> = null;

  /** The values the index holds (`populate`'s input). */
  events(): CalendarEvent[] {
    return [...this.#held.values()].map((held) => held.event);
  }

  /**
   * The row's stored values, one per id, as `eventValues(row, null)` answers
   * them (in the order each id was first stored), as of the last read: the
   * same list until a read changes one.
   */
  current(): readonly CalendarEvent[] {
    return this.#currentList ??= Object.freeze([...this.#current.values()]);
  }

  /** Read every cell of `row`; the changes against what the index holds. */
  readAll(row: SourceRow): IndexChanges {
    this.read = true;
    const ids = new Set<EventId>(this.#held.keys());
    this.#places.clear();
    this.#placesOf.clear();
    this.#columns.clear();
    this.#days.clear();
    const winners = new Map<EventId, Option<CalendarEvent>>();
    for (const cell of row.peekCells(null)) {
      if (!EVENT_SHAPE.probe(cell.value)) continue;
      const ev = cell.value;
      const key = cell.kind === "date" ? this.#dateKey(cell.date) : this.#dayKey(cell.dtr.start.date);
      let place = this.#places.get(key);
      if (place == null) {
        place = new Map();
        this.#places.set(key, place);
      }
      place.set(ev.id, { event: ev, order: cell.kind === "date" ? cell.date.start.dse : cell.dtr.start.mse });
      this.#placeOf(ev.id).add(key);
      // `eventValues`: a later cell wins the id (the timepoint lane is read last).
      winners.set(ev.id, ev);
      ids.add(ev.id);
    }
    for (const id of ids) if (!winners.has(id)) winners.set(id, null);
    return this.#settle(winners);
  }

  /**
   * Re-read the places `columns` name — and every other place an event
   * found there, or held there before, was held in (it may have moved) —
   * and answer the changes;
   * `null` when a place holds two copies of one event, or two places tie
   * for it (only a full read orders those), so the caller reads all.
   */
  readColumns(row: SourceRow, columns: readonly NaiveDate.Partial[]): Option<IndexChanges> {
    const pending: string[] = [];
    for (const column of columns) {
      pending.push(this.#dateKey(column));
      if (column.type === "day") pending.push(this.#dayKey(column.start));
    }
    const touched = new Set<EventId>();
    const seen = new Set<string>();
    for (let key = pending.pop(); key != null; key = pending.pop()) {
      if (seen.has(key)) continue;
      seen.add(key);
      const fresh = this.#readPlace(row, key);
      if (fresh == null) return null;
      for (const id of this.#places.get(key)?.keys() ?? []) {
        touched.add(id);
        const places = this.#placesOf.get(id);
        places?.delete(key);
        for (const other of places ?? []) if (!seen.has(other)) pending.push(other);
      }
      for (const id of fresh.keys()) {
        touched.add(id);
        const places = this.#placeOf(id);
        places.add(key);
        for (const other of places) if (!seen.has(other)) pending.push(other);
      }
      if (fresh.size > 0) this.#places.set(key, fresh);
      else this.#places.delete(key);
    }
    const winners = new Map<EventId, Option<CalendarEvent>>();
    for (const id of touched) {
      const places = this.#placesOf.get(id);
      if (places == null || places.size === 0) {
        this.#placesOf.delete(id);
        winners.set(id, null);
        continue;
      }
      const winner = this.#winner(id, places);
      if (winner == null) return null;
      winners.set(id, winner);
    }
    return this.#settle(winners);
  }

  /** The copy `eventValues` keeps of `id`: a timepoint place over a date
   *  place, then the greater order; `null` on a tie. */
  #winner(id: EventId, places: ReadonlySet<string>): Option<CalendarEvent> {
    let best: Option<{ readonly lane: number; readonly placed: Placed }> = null;
    let tie = false;
    for (const key of places) {
      const placed = this.#places.get(key)?.get(id);
      if (placed == null) continue;
      const lane = key.startsWith("t") ? 1 : 0;
      if (best == null || lane > best.lane || (lane === best.lane && placed.order > best.placed.order)) {
        best = { lane, placed };
        tie = false;
      } else if (lane === best.lane && placed.order === best.placed.order) {
        tie = true;
      }
    }
    return best == null || tie ? null : best.placed.event;
  }

  /** The events one place holds now, by id; `null` when it holds two copies of one. */
  #readPlace(row: SourceRow, key: string): Option<Map<EventId, Placed>> {
    const out = new Map<EventId, Placed>();
    const add = (value: SourceRow.Cell["value"], order: number): boolean => {
      if (!EVENT_SHAPE.probe(value)) return true;
      if (out.has(value.id)) return false;
      out.set(value.id, { event: value, order });
      return true;
    };
    const day = this.#days.get(key);
    if (day != null) {
      for (const cell of row.peekTimepointCells(new NaiveDate.Range(day, day.addDays(1)))) {
        if (!add(cell.value, cell.dtr.start.mse)) return null;
      }
      return out;
    }
    const column = this.#columns.get(key);
    if (column == null) return out;
    for (const cell of row.peekDateCells(column.toRange())) {
      if (cell.date.equals(column) && !add(cell.value, cell.date.start.dse)) return null;
    }
    return out;
  }

  /** Record what the index now holds; the puts and removes that differ by render signature. */
  #settle(winners: ReadonlyMap<EventId, Option<CalendarEvent>>): IndexChanges {
    const put: CalendarEvent[] = [];
    const remove: EventId[] = [];
    for (const [id, ev] of winners) {
      if (ev == null ? this.#current.delete(id) : this.#current.get(id) !== ev) {
        if (ev != null) this.#current.set(id, ev);
        this.#currentList = null;
      }
      const held = this.#held.get(id);
      if (ev == null) {
        if (held != null) {
          this.#held.delete(id);
          remove.push(id);
        }
        continue;
      }
      const sig = this.#sig(ev);
      // Same render signature: the index keeps what it holds (a re-fetch of
      // an unchanged event, an edit outside what the calendar paints).
      if (held != null && held.sig === sig) continue;
      this.#held.set(id, { event: ev, sig });
      put.push(ev);
    }
    return { put, remove };
  }

  #sig(ev: CalendarEvent): string {
    let sig = this.#sigs.get(ev);
    if (sig == null) {
      sig = EventsFacade.eventSignature(ev);
      this.#sigs.set(ev, sig);
    }
    return sig;
  }

  #placeOf(id: EventId): Set<string> {
    let places = this.#placesOf.get(id);
    if (places == null) {
      places = new Set();
      this.#placesOf.set(id, places);
    }
    return places;
  }

  #dateKey(column: NaiveDate.Partial): string {
    const key = "d" + column.toString();
    if (!this.#columns.has(key)) this.#columns.set(key, column);
    return key;
  }

  #dayKey(day: NaiveDate): string {
    const key = "t" + day.dse;
    if (!this.#days.has(key)) this.#days.set(key, day);
    return key;
  }
}

/** A source row's events, indexed by time, as event items. */
export class EventsFacade {
  static readonly #internals: EventsFacadeInternals = {
    dispose: (facade) => facade.#dispose(),
    writer: (facade) => facade.#writerFace(),
  };

  /** The writer face (`eventsWriterOf`), built on first use. */
  #writer: Option<EventsFacade.Writer> = null;

  #writerFace(): EventsFacade.Writer {
    this.#writer ??= Object.freeze({
      put: (event: CalendarEvent) => this.#put(event),
      remove: (id: EventId) => this.#remove(id),
      removeLocal: (id: EventId, date: NaiveDate.Partial) => this.#removeLocal(id, date),
      applyUpdate: (id: EventId, patch: EventPatch) => this.#applyUpdate(id, patch),
    });
    return this.#writer;
  }

  readonly #index: EventsIndex;
  /** Opaque data-owning origin used to hydrate base `EventItem`s — the same
   *  origin the index hydrates with, so derived items route writes correctly. */
  readonly #origin: EventOrigin;
  #dirty = true;
  /**
   * The cell columns announced changed since the last refresh
   * (`ColumnDataChanged.column`), by key; a refresh re-reads only these.
   * `null`: read every cell (the first read, or too many columns).
   */
  #dirtyColumns: Option<Map<string, NaiveDate.Partial>> = null;
  /** The row's stored events as the index holds them, and where each lives. */
  readonly #stored = new StoredEvents();
  /**
   * Furthest `NaiveDate` any range query has asked the recurrence
   * expander to cover. The index clears synthetic instances and resets
   * its own horizon on every `populate()` (rebuild), so the facade
   * tracks the view's requested horizon here and re-drives expansion to
   * it — the read surface yields recurring instances without any explicit
   * `expandRecurrenceUntil` caller (P4.2). Partial-day only; full-day
   * RRULE expansion remains gated in the index (separate slice).
   */
  #requestedExpansionUntil: Option<NaiveDate> = null;
  /** Horizon the index is confirmed expanded to, for the index generation
   *  it was confirmed in (`EventsIndex.generation`: a populate or a local
   *  mutation of the index starts a new one, so a new or edited master
   *  re-drives expansion). Monotonic within a generation. */
  #confirmed: Option<{ readonly indexGen: number; readonly until: NaiveDate }> = null;
  /** The expansion in flight (index generation, horizon, settlement), so
   *  concurrent kicks and `prepare` calls for a horizon it covers join it. */
  #inflight: Option<{ readonly indexGen: number; readonly target: NaiveDate; readonly done: Promise<void> }> = null;
  /** Failed expansions of the current index generation and the farthest
   *  horizon they were for: the query-driven re-drive retries a failed
   *  horizon up to `MAX_EXPANSION_FAILURES` times (each traced), then stops
   *  until the events change; each `prepare` retries it once more. */
  #failures: Option<{ readonly indexGen: number; readonly target: NaiveDate; readonly count: number; readonly error: Error }> = null;
  static readonly #MAX_EXPANSION_FAILURES = 3;
  /** How many expansions one `prepare` may wait on while the events keep
   *  changing before it rejects `SupersededError` (bounded progress). */
  static readonly #MAX_PREPARE_ROUNDS = 4;
  readonly #unsubscribe: () => void;
  /** Cell changes announced for the row (`ColumnDataChanged`), counted when
   *  announced — before the lazy rebuild reads them (`peekRevision`). */
  #cellChanges = 0;

  readonly #row: SourceRow;

  constructor(row: SourceRow) {
    eventsFacadeInternals.register(this, EventsFacade.#internals);
    this.#row = row;
    // Stamp the row's opaque origin (its data-owning source row),
    // not the projection id — so events route writes to the writable
    // source. The subscription below still keys on `row.id` (the
    // projection emits ColumnDataChanged against its own id).
    this.#origin = row.eventOrigin();
    // The index emits (its own `EventsChanged`), so it takes the sheet's full
    // sink through the package friend; the row's `sheetReads` sink is subscribe-only.
    this.#index = new EventsIndex(sheetOf(row).invalidationSink, this.#origin);
    this.#unsubscribe = row.sheetReads.invalidationSink.subscribe(
      (e: Invalidation) => {
        if (e instanceof ColumnDataChanged && e.rowId === row.id) {
          this.#dirty = true;
          this.#cellChanges++;
          const columns = this.#dirtyColumns;
          if (columns != null) {
            columns.set(e.column.toString(), e.column);
            if (columns.size > EventsFacade.#MAX_INCREMENTAL_COLUMNS) this.#dirtyColumns = null;
          }
        }
      },
    );
  }

  /** Stop listening to the row's store. Reached through `disposeEventsFacade`. */
  #dispose(): void {
    this.#unsubscribe();
  }

  /**
   * Monotonic: advances whenever a read of this facade, or of its row's
   * stored events, may answer differently — a cell change announced for the
   * row, or an index change (a rebuild, an expansion install, a master-slot
   * suppression). Equal revisions read equal results. A dirty facade
   * rebuilds first, as any read would, so the rebuild a change causes is
   * counted here and not by the read after it.
   */
  peekRevision(): number {
    this.#refreshIfDirty();
    return this.#cellChanges + this.#index.version;
  }

  // ── Queries (lazy-rebuild on dirty) ───────────────────────────────

  peek(id: EventId): Option<EventItem> {
    this.#refreshIfDirty();
    return this.#index.peek(id);
  }

  /**
   * Whether the item held under `id` is a stored event of the row
   * (`"stored"`: a cell, including a materialized occurrence override) or
   * an occurrence the recurrence expansion generated (`"expanded"`: no cell
   * of its own); `null` when the facade holds no item `id`.
   */
  peekProvenance(id: EventId): Option<"stored" | "expanded"> {
    this.#refreshIfDirty();
    return this.#index.peekProvenance(id);
  }

  has(id: EventId): boolean {
    this.#refreshIfDirty();
    return this.#index.has(id);
  }

  peekOnDate(dt: DateTime<Utc>): readonly EventItemTimed[] {
    this.#refreshIfDirty();
    // Cover occurrences ON this day. Expansion's `until` is inclusive
    // (`generateUpToDate` returns an occurrence dated `until`), so `dt.date`
    // alone would do; the day more is harmless headroom.
    this.#ensureExpansion(dt.date.addDays(1));
    return this.#index.peekOnDate(dt);
  }

  peekInRange(range: DateTime.Range<Utc>): readonly EventItemTimed[] {
    this.#refreshIfDirty();
    this.#ensureExpansion(range.end.date);
    return this.#index.peekInRange(range);
  }

  peekAllDayInRange(
    range: NaiveDate.Range,
  ): readonly EventItemAllDay[] {
    this.#refreshIfDirty();
    return this.#index.peekAllDayInRange(range);
  }

  peekAllDayEvents(): readonly EventItemAllDay[] {
    this.#refreshIfDirty();
    return Object.freeze([...this.#index.peekAllDayEvents()]);
  }

  /**
   * The row's stored events as `eventValues(row, null)` answers them (both
   * lanes, one value per id; in the order each id was first stored): a
   * reader that wants the stored values (the calendar read's raw lane, the
   * calendar-list provider) asks here instead of collecting the row's
   * history on every bind. Kept per change, as every read of the facade is
   * refreshed: an announced change re-reads the columns it names, not the
   * row's history. The same list until a change alters one.
   */
  peekStoredEvents(): readonly CalendarEvent[] {
    this.#refreshIfDirty();
    return this.#stored.current();
  }

  /** Partial-day events on a given NaiveDate (convenience). */
  eventsOn(date: NaiveDate.Partial): readonly CalendarEvent[] {
    return this.#cellEventsIn(date.toRange());
  }

  /** Every event (raw Event — no EventItem hydration) in a NaiveDate range. */
  eventsInRange(range: NaiveDate.Range): readonly CalendarEvent[] {
    return this.#cellEventsIn(range);
  }

  /** Look up a raw `Event` by id. */
  peekById(id: EventId): Option<CalendarEvent> {
    this.#refreshIfDirty();
    const item = this.#index.peek(id);
    return item ? item.inner : null;
  }

  async expandRecurrenceUntil(
    until: NaiveDate,
    invalidation: "announce" | "silent" = "announce",
  ): Promise<void> {
    this.#refreshIfDirty();
    await this.#index.expandRecurrenceUntil(until, invalidation);
  }

  diagnostics(): ReturnType<EventsIndex["diagnostics"]> {
    this.#refreshIfDirty();
    return Object.freeze(this.#index.diagnostics());
  }

  /**
   * Drive recurrence expansion so a range query's read surface yields
   * synthetic instances. Records the furthest requested horizon, then
   * kicks an async expansion (RRULE parse is async). The expander
   * announces its inserts on completion (`TimepointsChanged` /
   * `ViewportsInvalidated` — not `ColumnDataChanged`, so this does not
   * re-mark the facade dirty), which re-renders the view; the follow-up
   * read then sees the instances. Fire-and-forget by design — keeping the
   * query sync avoids the gesture-time stalls the legacy expanders hit.
   */
  #ensureExpansion(until: NaiveDate): void {
    this.#requestExpansion(until);
    this.#kickExpansion();
  }

  /**
   * **Recurrence readiness.** Resolves once this row's recurring masters are
   * expanded through `until` (exclusive) for the row's CURRENT events, so the
   * synchronous reads (`peekInRange`, `peekOnDate`) up to `until` return
   * every occurrence. The reads stay synchronous; a consumer awaits this
   * first, then reads.
   *
   * - Its own horizon: it joins the expansion in flight only when that one
   *   is for no farther than `until`; otherwise it expands to `until`
   *   itself, so it never waits on a farther horizon another view asked for.
   * - Current generation: when the events change while it waits (a rebuild
   *   or a local index mutation), it prepares the new events instead, and
   *   resolves as soon as `until` is covered for them.
   * - Bounded progress: after `MAX_PREPARE_ROUNDS` expansions without
   *   coverage (events changing faster than they expand) it rejects with
   *   `EventsFacade.SupersededError`; the changes are announced as usual,
   *   so a consumer prepares again on the next invalidation.
   * - Rejects with the expansion's own error when it failed twice (the
   *   failure it found, plus its own retry). Coverage is recorded only on
   *   success.
   * - Resolves only after the index committed the occurrences (and announced
   *   them, `TimepointsChanged`).
   */
  async prepare(until: NaiveDate): Promise<void> {
    this.#refreshIfDirty();
    this.#requestExpansion(until);
    let retried = false;
    for (let round = 0; ; round++) {
      if (this.#covers(until)) return;
      const failed = this.#failures;
      if (failed != null && failed.indexGen === this.#index.generation && failed.target.dse >= until.dse) {
        if (retried) throw failed.error;
        retried = true;
      }
      if (round >= EventsFacade.#MAX_PREPARE_ROUNDS) {
        throw new EventsFacade.SupersededError(this.#row.id, until);
      }
      const inflight = this.#inflight;
      const running = inflight != null && inflight.indexGen === this.#index.generation && inflight.target.dse <= until.dse
        ? inflight.done
        : this.#expand(until, inflight == null);
      try {
        await running;
      } catch {
        // Examined at the top of the next round (`#failures`).
      }
      // Events written while it ran rebuild the index now.
      this.#refreshIfDirty();
    }
  }

  #covers(until: NaiveDate): boolean {
    const confirmed = this.#confirmed;
    return confirmed != null && confirmed.indexGen === this.#index.generation &&
      confirmed.until.dse >= until.dse;
  }

  #requestExpansion(until: NaiveDate): void {
    if (
      !this.#requestedExpansionUntil ||
      until.dse > this.#requestedExpansionUntil.dse
    ) {
      this.#requestedExpansionUntil = until;
    }
  }

  /**
   * Query-driven: expand to the requested horizon unless an expansion is in
   * flight (it re-drives when it settles), the horizon is covered for the
   * current index generation, or it failed `MAX_EXPANSION_FAILURES` times in
   * this generation.
   */
  #kickExpansion(): void {
    if (this.#inflight != null) return;
    const target = this.#requestedExpansionUntil;
    if (!target || this.#covers(target)) return;
    const failed = this.#failures;
    if (
      failed != null && failed.indexGen === this.#index.generation &&
      failed.target.dse >= target.dse && failed.count >= EventsFacade.#MAX_EXPANSION_FAILURES
    ) return;
    this.#expand(target, true);
  }

  /**
   * Expand the index to `target`. Coverage is recorded only when it
   * succeeded under the index generation it started in (a superseded index
   * expansion writes nothing). `shared`: it becomes the in-flight expansion
   * others join, and re-drives the query horizon when it settles.
   */
  #expand(target: NaiveDate, shared: boolean): Promise<void> {
    const indexGen = this.#index.generation;
    const done = this.#index.expandRecurrenceUntil(target).then(
      () => {
        if (indexGen !== this.#index.generation) return;
        const confirmed = this.#confirmed;
        if (confirmed == null || confirmed.indexGen !== indexGen || confirmed.until.dse < target.dse) {
          this.#confirmed = { indexGen, until: target };
        }
        const failed = this.#failures;
        if (failed != null && failed.target.dse <= target.dse) this.#failures = null;
      },
      (err) => {
        const error = err instanceof Error ? err : new Error(String(err));
        if (indexGen === this.#index.generation) {
          const prior = this.#failures;
          const count = prior != null && prior.indexGen === indexGen ? prior.count + 1 : 1;
          const farthest = prior != null && prior.indexGen === indexGen && prior.target.dse > target.dse ? prior.target : target;
          this.#failures = { indexGen, target: farthest, count, error };
        }
        Tracing.warn("ui")("events-facade/expansion-failed", {
          rowId: this.#row.id.toString(),
          until: target.toString(),
          err: error.message,
        });
        throw error;
      },
    );
    if (!shared) return done;
    const settled = done.finally(() => {
      if (this.#inflight?.done === settled) this.#inflight = null;
      // Re-drive if the horizon advanced or the events changed.
      this.#kickExpansion();
    });
    // A query-driven kick has no awaiter: a failure is recorded in
    // `#failures` (and traced) for the re-drive and `prepare`.
    settled.catch(() => {});
    this.#inflight = { indexGen, target, done: settled };
    return settled;
  }

  // ── Writers (`eventsWriterOf`; through the row's event chokepoint) ──

  /** `EventsFacade.Writer.put`. */
  async #put(event: CalendarEvent): Promise<void> {
    // Route through the row's event chokepoint: it places the cell in the lane
    // its temporal kind demands (timed → datetime/custom; all-day →
    // date/semantic) and, given the prior indexed copy, clears the stale lane
    // on an all-day⇄timed toggle or a day move — one entry per id, no ghost.
    const prev = this.peekById(event.id);
    await sourceWriterOf(this.#row).writeEvent(event, prev, EventsFacade.ACTOR);
  }

  /** `EventsFacade.Writer.remove`. */
  async #remove(id: EventId): Promise<void> {
    const ev = this.peekById(id);
    if (ev == null) return;
    const writer = sourceWriterOf(this.#row);
    // The lane the event's temporal kind placed it in (`writeEvent`'s rule):
    // a timed event is a timepoint cell keyed by its id, an all-day one the
    // date cell of its start day.
    if (CellLane.temporalKindOfEvent(ev) === "timepoint") {
      await writer.clearTimepointCell(CellId.of(ev.id), EventsFacade.ACTOR);
    } else {
      await writer.clear(NaiveDate.Partial.fromDate(ev.startDate ?? ev.start.date), CellId.of(ev.id), EventsFacade.ACTOR);
    }
    // Accepted: a copy stranded in the other lane (lane drift on create or
    // hydration) goes too, locally, as `removeLocal` clears it.
    writer.local.clearEvent(ev, EventsFacade.ACTOR);
  }

  /** `EventsFacade.Writer.applyUpdate`. */
  async #applyUpdate(
    id: EventId,
    patch: EventPatch,
  ): Promise<Option<EventItem>> {
    this.#refreshIfDirty();
    const item = this.#index.peek(id);
    invariant(item, "EventsFacade.applyUpdate: event not in index", {
      id,
      rowId: this.#row.id.toString(),
    });
    await this.#put(patch.paint(item).inner);
    // `put` fired `ColumnDataChanged` which marked the
    // facade dirty; use `find()` (not `index.find`) so the next
    // read rebuilds from the freshly-written cell.
    return this.peek(id);
  }

  /** `EventsFacade.Writer.removeLocal`. */
  #removeLocal(id: EventId, date: NaiveDate.Partial): void {
    // Clear from whichever lane the event's temporal kind placed it in; fall
    // back to the date-keyed lane when the event isn't indexed (best effort).
    const ev = this.peekById(id);
    if (ev != null) sourceWriterOf(this.#row).local.clearEvent(ev, EventsFacade.ACTOR);
    else sourceWriterOf(this.#row).local.clearCell(date, CellId.of(id), EventsFacade.ACTOR);
  }

  // ── Rebuild ────────────────────────────────────────────────────────

  /** Re-entrancy witness — if `refreshIfDirty` is called nested
   *  (e.g. a listener fires another query that triggers another
   *  refresh), emit `witness/reentry` so the operator can see the
   *  cycle in the trace. "log" mode keeps existing behavior. */
  static readonly #refreshReentrancy = new Tracing.Witness.Reentrancy({
    name: "events-facade/refresh",
    onReentry: "log",
  });

  /** Rate-limit witness — if a single facade rebuilds more than
   *  20 times in 500ms, something is thrashing past the dirty-hash
   *  short-circuit (e.g. an upstream is churning the cell set). */
  static readonly #refreshRateLimit = new Tracing.Witness.RateLimit({
    name: "events-facade/refresh",
    windowMs: 500,
    threshold: 20,
  });

  /** Past this many changed columns a refresh reads every cell at once. */
  static readonly #MAX_INCREMENTAL_COLUMNS = 64;

  #refreshIfDirty(): void {
    if (!this.#dirty) return;
    const exitReentry = EventsFacade.#refreshReentrancy.enter({
      rowId: this.#row.id.toString(),
    });
    try {
      EventsFacade.#refreshRateLimit.observe(this.#row.id.toString(), null);
      Tracing.span("events-facade/refresh", (s) => {
        // Re-read only the announced columns (and wherever an event they
        // name was held before); every cell on the first read, past
        // `MAX_INCREMENTAL_COLUMNS`, or when the columns cannot say which
        // copy of an event wins (`StoredEvents.readColumns`).
        const columns = this.#dirtyColumns;
        const first = !this.#stored.read;
        const changes = (columns == null ? null : this.#stored.readColumns(this.#row, [...columns.values()])) ??
          this.#stored.readAll(this.#row);
        this.#dirty = false;
        this.#dirtyColumns = new Map();
        s.record("rowId", this.#row.id.toString());
        s.record("incremental", columns != null);
        s.record("changed", changes.put.length + changes.remove.length);
        // Same events by render signature: nothing to rebuild, nothing to
        // announce (a re-fetch of unchanged events, an edit outside what
        // the calendar paints).
        if (!first && changes.put.length === 0 && changes.remove.length === 0) return;
        if (first) this.#index.populate(this.#stored.events());
        else this.#index.apply(changes);
        // An edit of a recurring master starts a new index generation (its
        // occurrences re-derive). Re-drive expansion to whatever range the
        // view has already asked for, so instances don't vanish after it.
        this.#kickExpansion();
      });
    } finally {
      exitReentry();
    }
  }

  #cellEventsIn(range: NaiveDate.Range): readonly CalendarEvent[] {
    return this.#collectEvents(range);
  }

  /**
   * Collect events from BOTH cell lanes, deduped by id — the semantic date
   * lane (all-day) and the timepoint lane (timed). Shared with the list
   * aggregator and the calendar-list provider via `eventValues`, so every
   * event reader unions the lanes the deconfliated substrate splits an event
   * across. The `SourceRow.writeEvent` chokepoint keeps an event in exactly one
   * lane, so the dedup is defensive rather than load-bearing.
   */
  #collectEvents(range: Option<NaiveDate.Range>): CalendarEvent[] {
    return eventValues(this.#row, range);
  }
}

/**
 * Types and values that go with `EventsFacade`: `Writer`, `SupersededError`,
 * `ACTOR`, `eventSignature`, `hashEvents`, `factory`.
 */
export namespace EventsFacade {
  /**
   * What `EventsFacade.prepare` rejects with when the row's events changed
   * while it waited: the preparation was for events that are gone. Prepare
   * again for the current ones.
   */
  export class SupersededError extends Error {
    constructor(readonly rowId: RowLocalId, readonly until: NaiveDate) {
      super(`EventsFacade.prepare(${until.toString()}) on ${rowId.toString()}: the row's events changed while preparing`);
      this.name = "EventsFacade.SupersededError";
    }
  }

  /** The writers `eventsWriterOf(facade)` hands the SDK (see there). */
  export interface Writer {
    /**
     * Upsert an event, persisted through the row's event chokepoint: the
     * cell lands in the lane the event's temporal kind demands (timed → the
     * timepoint lane, all-day → the date lane), and a toggle or a day move
     * clears the lane the held copy was in. Rejects when the backend refuses,
     * after both lanes are put back.
     */
    put(event: CalendarEvent): Promise<void>;
    /**
     * Remove the held event `id`, persisted: its cell is cleared in the lane
     * `put` placed it in and the clear is sent; a refusal puts it back and
     * rejects. A no-op when the facade holds no event `id`.
     */
    remove(id: EventId): Promise<void>;
    /**
     * Remove event `id` locally, from both lanes; nothing is sent. An event
     * the facade does not hold is cleared from the date cell of `date`.
     */
    removeLocal(id: EventId, date: NaiveDate.Partial): void;
    /**
     * Patch the held event `id` and persist the result as `put` does;
     * resolves the re-read item. Throws when the facade holds no event `id`
     * (a race with a server delete, or a drifted index).
     */
    applyUpdate(id: EventId, patch: EventPatch): Promise<Option<EventItem>>;
  }

  /**
   * Invalidation actor used by the index for optimistic-write
   * suppression — consumers testing `Invalidation.origin === ACTOR`
   * to skip re-renders they triggered themselves.
   */
  export const ACTOR: typeof EventsIndex.ACTOR = EventsIndex.ACTOR;

  /**
   * Stable per-event signature for the dirty-check short-circuit
   * in `refreshIfDirty`. Includes every field whose change should
   * trigger a re-render: identity (id, calendarId), time bounds,
   * full-day bounds, recurrence pointers, and timezone. Optional
   * fields collapse to "" so order in the join is stable.
   *
   * Volatile-but-non-rendering fields (e.g. `description` on a
   * collapsed event) are intentionally NOT part of the
   * signature — those edits don't change what the calendar
   * paints, so re-render-suppression is correct. If a future
   * renderer surfaces `description`, add it here.
   */
  export function eventSignature(e: CalendarEvent): string {
    return [
      e.id,
      e.title,
      e.start?.mse ?? "",
      e.end?.mse ?? "",
      e.startDate?.dse ?? "",
      e.endDate?.dse ?? "",
      e.calendarId,
      e.authId,
      (e.recurrence ?? []).join(","),
      e.recurringEventId ?? "",
      e.timezone ?? "",
      e.eventType ?? "",
      e.category ?? "",
      e.suggestedCategory ?? "",
      e.useSuggestedCategory == null ? "" : String(e.useSuggestedCategory),
    ].join("|");
  }

  export function hashEvents(events: readonly CalendarEvent[]): string {
    // Sort signatures so the hash is stable regardless of the
    // order `row.peekCells(null)` returns events in (the multi-card
    // decoder doesn't guarantee a stable order across refreshes).
    const sigs = events.map((event) => eventSignature(event));
    sigs.sort();
    return sigs.join(";");
  }

  export const factory: FacadeFactory<EventsFacade> = deepFrozen({
    key: EVENTS_FACADE,
    build(row: SourceRow): Option<EventsFacade> {
      if (row instanceof ListDataSource) return null;
      if (row.descriptor.role !== "source") return null;
      if (row.descriptor.dtype !== "obj") return null;
      if (row.descriptor.cellCardinality !== "multi") return null;
      return new EventsFacade(row);
    },
  });
}
